// src/pages/partners/partners-logged-in/routes/groups/index.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  Divider,
  CardHeader,
  CardContent,
  TextField,
  IconButton,
  Button,
  Chip,
  Avatar,
  Autocomplete,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../../redux/store";
import GlassContainer from "../../../components/liquid-glass";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SearchIcon from "@mui/icons-material/Search";
import { PartnerTrainerGroup, User } from "../../../../../models/types";

import {
  fetchAllClientsWithWorkouts,
  updateTrainerGroups,
} from "../../../../../redux/thunks/thunk-partners";

import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

export const poolData = {
  UserPoolId: "us-east-2_nECr2VdPc",
  ClientId: "6jd4v12t5keoj2eupcrjkcia2c",
};
const userPool = new CognitoUserPool(poolData);

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const MAX_SLOTS = 9;

/** Minimal id generator — no imports. */
let __gid = Math.floor(Math.random() * 1e6);
function makeId(prefix = "grp") {
  __gid = (__gid + 1) % 1_000_000;
  const t = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${t}_${__gid.toString(36)}_${r}`;
}

function makeGlassBg(isDark: boolean) {
  return isDark
    ? "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)"
    : "linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 100%)";
}

function normalizeClients(
  sourceA: any[] | null,
  sourceB: any[] | null
): User[] {
  const a = (sourceA ?? []) as any[];
  if (a.length && (a[0]?.userId || a[0]?.clientUserId)) {
    return a.map((c: any) => ({
      userId: c.userId ?? c.clientUserId,
      name: c.name ?? c.email ?? c.userId ?? c.clientUserId,
      email: c.email ?? "",
      templates: [],
      workouts: c.workouts ?? [],
      meals: [],
      waterEntries: [],
      goals: {
        caloriesGoal: 0,
        proteinGoal: 0,
        carbsGoal: 0,
        fatsGoal: 0,
        trainingGoal: "maintain",
      },
      height: 0,
      weight: 0,
      gender: "male",
      birthday: new Date(),
      activityLevel: "sedentary",
    })) as User[];
  }

  const b = (sourceB ?? []) as any[];
  return b.map((c: any) => ({
    userId: c.clientUserId,
    name: c.name ?? c.clientUserId,
    email: "",
    templates: [],
    workouts: [],
    meals: [],
    waterEntries: [],
    goals: {
      caloriesGoal: 0,
      proteinGoal: 0,
      carbsGoal: 0,
      fatsGoal: 0,
      trainingGoal: "maintain",
    },
    height: c.height ?? 0,
    weight: c.weight ?? 0,
    gender: c.gender ?? "male",
    birthday: c.birthday ? new Date(c.birthday) : new Date(),
    activityLevel: c.activityLevel ?? "sedentary",
  })) as User[];
}

// Compare utilities (guarded sync & persist)
function sameStringArray(a: string[] = [], b: string[] = []) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
function groupsShallowEqual(
  a: PartnerTrainerGroup[] = [],
  b: PartnerTrainerGroup[] = []
) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ga = a[i],
      gb = b[i];
    if (!ga || !gb) return false;
    if (ga.id !== gb.id) return false;
    if (ga.name !== gb.name) return false;
    if (!sameStringArray(ga.clientUserIds ?? [], gb.clientUserIds ?? []))
      return false;
  }
  return true;
}

type LocalGroup = PartnerTrainerGroup & {
  _isNew?: boolean;
  _isEditing?: boolean;
};

export default function Groups() {
  const dispatch = useDispatch<AppDispatch>();

  // ── global state
  const darkMode = useSelector((s: RootState) => !!s.partners.darkMode);
  const partner = useSelector(
    (s: RootState) => s.partners.partnerData?.partnerData
  ) as User | null;
  const trainerUserId = partner?.userId ?? "";
  const existingGroups = (partner?.trainingGroups ??
    []) as PartnerTrainerGroup[];

  const clientsWithWorkouts = useSelector(
    (s: RootState) => s.partners.clientsWithWorkouts
  ) as any[] | null;
  const partnerClients = (partner as any)?.partnerClients ?? null;

  // ── derived colors (explicit per your request)
  const fg = darkMode ? "#ffffff" : "#000000";
  const fgMuted = darkMode ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.56)";
  const dividerCol = darkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)";
  const outlineCol = darkMode ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.20)";
  const outlineColHover = darkMode
    ? "rgba(255,255,255,0.35)"
    : "rgba(0,0,0,0.35)";

  // ── token for authenticated calls
  const [idToken, setIdToken] = useState<string>("");
  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) return;
    currentUser.getSession((err: any, session: CognitoUserSession | null) => {
      if (err) {
        console.error("Error retrieving session:", err);
        return;
      }
      if (session && session.isValid()) {
        setIdToken(session.getIdToken().getJwtToken());
      }
    });
  }, []);

  // ── pull clients list
  useEffect(() => {
    if (!idToken || !trainerUserId) return;
    const key = `avro:groups:fetched:${trainerUserId}`;
    const alreadyFetched =
      typeof window !== "undefined" &&
      window.sessionStorage.getItem(key) === "1";
    if (alreadyFetched) return;
    try {
      window.sessionStorage.setItem(key, "1");
    } catch {}
    dispatch(
      fetchAllClientsWithWorkouts({ idToken, partnerId: trainerUserId })
    );
  }, [dispatch, idToken, trainerUserId]);

  const clients = useMemo(
    () => normalizeClients(clientsWithWorkouts, partnerClients),
    [clientsWithWorkouts, partnerClients]
  );

  // ── local editable groups (sync w/ partner.trainingGroups)
  const [groups, setGroups] = useState<LocalGroup[]>(() =>
    (existingGroups ?? []).slice(0, MAX_SLOTS).map((g) => ({
      ...g,
      _isEditing: false,
    }))
  );

  // Which empty slot should open "create group" UI (from Quick Create)?
  const [createAtIndex, setCreateAtIndex] = useState<number | null>(null);

  // Guarded sync from store → local groups
  useEffect(() => {
    const current = groups
      .map(
        (g) =>
          g && {
            id: g.id,
            trainerUserId: g.trainerUserId,
            name: g.name,
            clientUserIds: g.clientUserIds ?? [],
            createdAt: g.createdAt,
            updatedAt: g.updatedAt,
            notes: g.notes,
          }
      )
      .filter(Boolean) as PartnerTrainerGroup[];

    const incoming = (existingGroups ?? []).slice(0, MAX_SLOTS);
    if (!groupsShallowEqual(current, incoming)) {
      setGroups(
        incoming.map((g) => ({
          ...g,
          _isEditing: false,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingGroups]);

  // ── derived map for quick lookups
  const clientMap = useMemo(() => {
    const m = new Map<string, User>();
    clients.forEach((c) => m.set(c.userId, c));
    return m;
  }, [clients]);

  // ─────────────────────────────────────────────────────────
  // Persist to backend (guarded against no-op)
  // ─────────────────────────────────────────────────────────
  const persistGroups = useCallback(
    (next: LocalGroup[]) => {
      if (!idToken || !trainerUserId) return;

      const payload: PartnerTrainerGroup[] = (
        next.filter(Boolean) as LocalGroup[]
      )
        .slice(0, MAX_SLOTS)
        .map(({ _isNew, _isEditing, ...g }) => ({
          ...g,
          clientUserIds: g.clientUserIds ?? [],
        }));

      const incoming = (existingGroups ?? []).slice(0, MAX_SLOTS);
      if (groupsShallowEqual(payload, incoming)) return;

      dispatch(
        updateTrainerGroups({
          idToken,
          trainerUserId,
          groups: payload,
        })
      );
    },
    [dispatch, idToken, trainerUserId, existingGroups]
  );

  // ─────────────────────────────────────────────────────────
  // Drag helpers (LEFT list → RIGHT slot)
  // ─────────────────────────────────────────────────────────
  const onClientDragStart = (ev: React.DragEvent, clientUserId: string) => {
    ev.dataTransfer.setData("text/plain", clientUserId);
    ev.dataTransfer.effectAllowed = "move";
  };

  const onDroppableDragOver = (ev: React.DragEvent) => {
    ev.preventDefault();
    ev.dataTransfer.dropEffect = "move";
  };

  const addClientToGroup = (slotIndex: number, clientUserId: string) => {
    setGroups((prev) => {
      const next = [...prev];
      const slot = next[slotIndex];
      if (!slot) return prev; // must create group first
      const cur = slot.clientUserIds ?? [];
      if (cur.includes(clientUserId)) return prev; // already present

      next[slotIndex] = {
        ...slot,
        clientUserIds: [...cur, clientUserId],
        updatedAt: new Date().toISOString(),
      };
      persistGroups(next);
      return next;
    });
  };

  const onDroppableDrop = (ev: React.DragEvent, slotIndex: number) => {
    ev.preventDefault();
    const clientId = ev.dataTransfer.getData("text/plain");
    if (!clientId) return;
    addClientToGroup(slotIndex, clientId);
  };

  // ─────────────────────────────────────────────────────────
  // Slot actions
  // ─────────────────────────────────────────────────────────
  const createGroupAt = (slotIndex: number, name: string) => {
    if (!name.trim()) return;
    const now = new Date().toISOString();
    setGroups((prev) => {
      const next = [...prev];
      const newGroup: LocalGroup = {
        id: makeId(),
        trainerUserId,
        name: name.trim(),
        clientUserIds: [],
        createdAt: now,
        updatedAt: now,
        _isNew: true,
        _isEditing: false,
      };
      next[slotIndex] = newGroup;
      if (next.length > MAX_SLOTS) next.length = MAX_SLOTS;
      persistGroups(next);
      return next;
    });
  };

  const renameGroup = (slotIndex: number, newName: string) => {
    setGroups((prev) => {
      const next = [...prev];
      if (!next[slotIndex]) return prev;
      next[slotIndex] = {
        ...next[slotIndex],
        name: newName,
        updatedAt: new Date().toISOString(),
      };
      return next;
    });
  };

  const toggleEdit = (slotIndex: number, on?: boolean) => {
    setGroups((prev) => {
      const next = [...prev];
      if (!next[slotIndex]) return prev;
      next[slotIndex] = {
        ...next[slotIndex],
        _isEditing: on ?? !next[slotIndex]._isEditing,
      };
      return next;
    });
  };

  const saveSlot = (slotIndex: number) => {
    setGroups((prev) => {
      const next = [...prev];
      if (!next[slotIndex]) return prev;
      next[slotIndex] = {
        ...next[slotIndex],
        _isEditing: false,
        _isNew: false,
      };
      persistGroups(next);
      return next;
    });
  };

  const removeClientFromGroup = (slotIndex: number, clientUserId: string) => {
    setGroups((prev) => {
      const next = [...prev];
      const slot = next[slotIndex];
      if (!slot) return prev;
      const cur = slot.clientUserIds ?? [];
      if (!cur.includes(clientUserId)) return prev;

      next[slotIndex] = {
        ...slot,
        clientUserIds: cur.filter((id) => id !== clientUserId),
        updatedAt: new Date().toISOString(),
      };
      persistGroups(next);
      return next;
    });
  };

  const deleteGroup = (slotIndex: number) => {
    setGroups((prev) => {
      const next = [...prev];
      if (!next[slotIndex]) return prev;
      next[slotIndex] = undefined as unknown as LocalGroup; // leave empty slot
      persistGroups(next.filter(Boolean) as LocalGroup[]); // backend stores only real groups
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────
  // UI: left list item
  // ─────────────────────────────────────────────────────────
  const ClientRow = ({ c }: { c: User }) => {
    return (
      <Box
        draggable
        onDragStart={(e) => onClientDragStart(e, c.userId)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.75,
          borderRadius: 2,
          cursor: "grab",
          userSelect: "none",
          color: fg,
          "&:hover": {
            backgroundColor: darkMode
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.06)",
          },
        }}
      >
        <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
          {(c.name ?? c.email ?? "?").slice(0, 1)}
        </Avatar>
        <Typography variant="body2" fontWeight={600} sx={{ color: fg }}>
          {c.name ?? c.email ?? c.userId}
        </Typography>
      </Box>
    );
  };

  // ─────────────────────────────────────────────────────────
  // UI: group slot (droppable Box INSIDE GlassContainer)
  // ─────────────────────────────────────────────────────────
  const Slot = ({
    idx,
    startCreate,
    onConsumeCreate,
  }: {
    idx: number;
    startCreate: boolean;
    onConsumeCreate: () => void;
  }) => {
    const g = groups[idx];
    const [isCreating, setIsCreating] = useState(false);
    const [nameDraft, setNameDraft] = useState("");
    const [tempName, setTempName] = useState("");

    // If Quick Create targeted this slot, open the name prompt
    useEffect(() => {
      if (!g && startCreate && !isCreating) {
        setIsCreating(true);
        onConsumeCreate();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startCreate, g, isCreating]);

    useEffect(() => {
      if (g) {
        setTempName(g.name);
        setIsCreating(false);
        setNameDraft("");
      }
    }, [g]);

    const membersSet = new Set(g?.clientUserIds ?? []);
    const searchableOptions = useMemo(
      () => clients.filter((c) => !membersSet.has(c.userId)),
      [clients, membersSet]
    );

    const handleAddBySearch = (_: any, selected: User | null) => {
      if (!selected) return;
      addClientToGroup(idx, selected.userId);
    };

    // Occupied slot
    if (g) {
      const members = (g.clientUserIds ?? [])
        .map((id) => clientMap.get(id))
        .filter(Boolean) as User[];

      return (
        <GlassContainer
          blur={22}
          radius={18}
          sx={{
            minHeight: 180,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            background: makeGlassBg(darkMode),
            color: fg,
          }}
        >
          <CardHeader
            title={
              g._isEditing ? (
                <TextField
                  size="small"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  sx={{
                    "& .MuiInputBase-root": { borderRadius: 2 },
                    "& .MuiInputBase-input": { color: fg },
                    "& .MuiInputLabel-root": { color: fgMuted },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: outlineCol,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: outlineColHover,
                    },
                  }}
                />
              ) : (
                <Typography variant="h6" fontWeight={800} sx={{ color: fg }}>
                  {g.name}
                </Typography>
              )
            }
            action={
              <Stack direction="row" alignItems="center" gap={0.5}>
                {g._isEditing ? (
                  <IconButton
                    onClick={() => {
                      renameGroup(idx, tempName.trim());
                      saveSlot(idx);
                    }}
                    sx={{ color: fgMuted }}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={() => toggleEdit(idx, true)}
                    sx={{ color: fgMuted }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  onClick={() => deleteGroup(idx)}
                  sx={{ color: fgMuted }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
            sx={{
              pb: 0,
              "& .MuiCardHeader-subheader": { color: fgMuted },
            }}
          />
          <CardContent sx={{ pt: 1, pb: 1 }}>
            <Autocomplete
              size="small"
              options={searchableOptions}
              getOptionLabel={(o) => o.name ?? o.email ?? o.userId}
              onChange={handleAddBySearch}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Add client..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon sx={{ mr: 0.5, color: fgMuted }} />
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": { borderRadius: 2 },
                    "& .MuiInputBase-input": { color: fg },
                    "& .MuiInputBase-input::placeholder": {
                      color: fgMuted,
                      opacity: 1,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: outlineCol,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: outlineColHover,
                    },
                    "& .MuiSvgIcon-root": { color: fgMuted },
                  }}
                />
              )}
              sx={{
                mb: 1,
                "& .MuiAutocomplete-paper": {
                  color: fg,
                  background: darkMode ? "#1f1f1f" : "#fff",
                },
              }}
            />

            <Divider sx={{ mb: 1, borderColor: dividerCol }} />

            <Box
              onDragOver={onDroppableDragOver}
              onDrop={(e) => onDroppableDrop(e, idx)}
              sx={{
                p: 1,
                borderRadius: 2,
                minHeight: 80,
                border: "1.5px dashed",
                borderColor: darkMode
                  ? "rgba(255,255,255,0.18)"
                  : "rgba(0,0,0,0.18)",
                color: fg,
              }}
            >
              {members.length === 0 ? (
                <Typography variant="body2" sx={{ color: fgMuted }}>
                  Drag clients here or use the search to add.
                </Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {members.map((m) => (
                    <Chip
                      key={m.userId}
                      avatar={
                        <Avatar sx={{ width: 22, height: 22, fontSize: 12 }}>
                          {(m.name ?? "?").slice(0, 1)}
                        </Avatar>
                      }
                      label={m.name ?? m.email ?? m.userId}
                      onDelete={() => removeClientFromGroup(idx, m.userId)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 700,
                        "& .MuiChip-label": { color: fg },
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </CardContent>

          <Box sx={{ px: 2, pb: 1, mt: "auto" }}>
            <Typography variant="caption" sx={{ color: fgMuted }}>
              Tip: Drop clients from the left list onto this card.
            </Typography>
          </Box>
        </GlassContainer>
      );
    }

    // Empty slot (with optional "create group" prompt)
    return (
      <GlassContainer
        blur={22}
        radius={18}
        sx={{
          minHeight: 180,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: makeGlassBg(darkMode),
          color: fg,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            px: 2,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1.5px dashed",
            borderColor: darkMode
              ? "rgba(255,255,255,0.25)"
              : "rgba(0,0,0,0.25)",
            borderRadius: 2,
          }}
        >
          {isCreating ? (
            <Stack
              alignItems="center"
              gap={1}
              sx={{ width: "100%", maxWidth: 360 }}
            >
              <TextField
                autoFocus
                fullWidth
                label="Group name"
                size="small"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = nameDraft.trim();
                    if (v) {
                      createGroupAt(idx, v);
                      setIsCreating(false);
                      setNameDraft("");
                    }
                  }
                }}
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    color: fg,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: outlineCol,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: outlineColHover,
                    },
                  },
                }}
                InputLabelProps={{ sx: { color: fgMuted } }}
              />
              <Stack direction="row" gap={1}>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  onClick={() => {
                    const v = nameDraft.trim();
                    if (!v) return;
                    createGroupAt(idx, v);
                    setIsCreating(false);
                    setNameDraft("");
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  Create Group
                </Button>
                <Button
                  variant="text"
                  onClick={() => setIsCreating(false)}
                  sx={{ borderRadius: 2, color: fg }}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack alignItems="center" gap={1}>
              <GroupAddIcon sx={{ color: fgMuted }} />
              <Typography
                variant="body2"
                sx={{ color: fgMuted }}
                textAlign="center"
              >
                Empty slot
              </Typography>
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="outlined"
                onClick={() => setIsCreating(true)}
                sx={{
                  borderRadius: 2,
                  color: fg,
                  borderColor: outlineCol,
                  "&:hover": {
                    borderColor: outlineColHover,
                    background: "transparent",
                  },
                }}
              >
                New Group
              </Button>
            </Stack>
          )}
        </Box>
      </GlassContainer>
    );
  };

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: "calc(100vh - 72px)",

        px: 1,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 3fr" },
        gap: 1,
        color: fg, // ensure all descendants inherit readable color
      }}
    >
      {/* LEFT: Clients list */}
      <GlassContainer
        blur={22}
        radius={18}
        sx={{
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background: makeGlassBg(darkMode),
          color: fg,
          "&:hover": {
            transform: "scale(1)",
          },
          mb: 10,
        }}
      >
        <CardHeader
          titleTypographyProps={{ sx: { color: fg, fontWeight: 800 } }}
          subheaderTypographyProps={{ sx: { color: fgMuted } }}
          title="Clients"
          subheader="Drag a client into a group →"
          sx={{ pb: 0 }}
        />
        <Divider sx={{ borderColor: dividerCol }} />
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto", pt: 1 }}>
          {clients.length === 0 ? (
            <Typography variant="body2" sx={{ color: fgMuted }}>
              No clients yet.
            </Typography>
          ) : (
            <Stack gap={0.25}>
              {clients.map((c) => (
                <ClientRow key={c.userId} c={c} />
              ))}
            </Stack>
          )}
        </CardContent>
      </GlassContainer>

      {/* RIGHT: 3x3 groups grid */}
      <GlassContainer
        blur={22}
        radius={18}
        sx={{
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background: makeGlassBg(darkMode),
          color: fg,
          "&:hover": {
            transform: "scale(1)",
          },
          mb: 10,
        }}
      >
        <CardHeader
          titleTypographyProps={{ sx: { color: fg, fontWeight: 800 } }}
          subheaderTypographyProps={{ sx: { color: fgMuted } }}
          title="Groups"
          subheader="Up to 9 groups"
          sx={{ pb: 0 }}
        />
        <Divider sx={{ borderColor: dividerCol }} />
        <CardContent sx={{ flex: 1, minHeight: 0, overflow: "hidden", pt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "1fr 1fr 1fr",
              },
              gridAutoRows: "minmax(200px, 1fr)",
              gap: 1,
              height: "100%",
              overflowY: "auto",
              pb: 1,
              color: fg,
            }}
          >
            {Array.from({ length: MAX_SLOTS }).map((_, idx) => (
              <Slot
                key={idx}
                idx={idx}
                startCreate={createAtIndex === idx}
                onConsumeCreate={() => setCreateAtIndex(null)}
              />
            ))}
          </Box>
        </CardContent>
      </GlassContainer>
    </Box>
  );
}
